import { useState, useEffect } from "react";
import type { User } from "../../types/user";
import type { KitRecord } from "../../types/orders";
import { listUsers } from "../../api/users";

interface AssignDoctorPanelProps {
  kit: KitRecord | null;
  mode: "assign" | "reassign";
  onClose: () => void;
  onConfirm: (doctorId: string, doctorName: string) => void;
}

export function AssignDoctorPanel({ kit, mode, onClose, onConfirm }: Readonly<AssignDoctorPanelProps>) {
  const [doctors, setDoctors] = useState<User[]>([]);
  const [selectedId, setSelectedId] = useState("");
  const [loadingDoctors, setLoadingDoctors] = useState(false);

  useEffect(() => {
    if (!kit) return;
    setSelectedId(mode === "reassign" && kit.assigned_doctor_id ? kit.assigned_doctor_id : "");
    setLoadingDoctors(true);
    listUsers()
      .then((users) => setDoctors(users.filter((u) => u.role === "doctor" && u.status === "active")))
      .catch(() => setDoctors([]))
      .finally(() => setLoadingDoctors(false));
  }, [kit, mode]);

  if (!kit) return null;

  const selectedDoctor = doctors.find((d) => d.id === selectedId);
  const isChanged = mode === "reassign" ? selectedId !== kit.assigned_doctor_id : selectedId !== "";
  const buttonLabel = mode === "reassign" ? "Save and reassign" : "Assign and Send";

  return (
    <>
      {/* Overlay */}
      <button
        type="button"
        className="fixed inset-0 bg-overlay z-40 cursor-default"
        onClick={onClose}
        aria-label="Close panel"
      />

      {/* Slide panel */}
      <div className="fixed top-0 right-0 h-full w-[480px] bg-bg-primary z-50 flex flex-col p-5">
        {/* Header */}
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-xl font-semibold text-text-primary">Assign Result</h2>
          <button onClick={onClose} className="text-text-tertiary hover:text-text-secondary">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M6 6l12 12M18 6l-12 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="flex-1">
          <div className="flex gap-5 mb-5">
            <div className="flex-1">
              <span className="text-sm text-text-tertiary">Order ID</span>
              <p className="text-sm text-text-primary">{kit.order_id}</p>
            </div>
            <div className="flex-1">
              <span className="text-sm text-text-tertiary">Kit ID</span>
              <p className="text-sm text-text-primary">{kit.kit_code}</p>
            </div>
          </div>

          {loadingDoctors ? (
            <div className="h-[51px] rounded-2xl border border-stroke-primary bg-bg-secondary animate-pulse" />
          ) : (
            <select
              value={selectedId}
              onChange={(e) => setSelectedId(e.target.value)}
              className="w-full h-[51px] rounded-2xl border border-stroke-primary bg-bg-primary px-4 text-base font-medium text-text-primary outline-none focus:border-brand-brown"
            >
              <option value="" className="text-text-tertiary">Select Doctor</option>
              {doctors.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name} {d.surname}
                </option>
              ))}
            </select>
          )}
        </div>

        {/* Footer button */}
        <button
          onClick={() => {
            if (selectedDoctor) {
              onConfirm(selectedDoctor.id, `${selectedDoctor.name} ${selectedDoctor.surname}`);
            }
          }}
          disabled={!isChanged || !selectedId}
          className="h-[51px] w-full rounded-2xl text-base font-semibold text-text-btn bg-btn-action disabled:bg-btn-primary-disabled"
        >
          {buttonLabel}
        </button>
      </div>
    </>
  );
}
